import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import logo from "@/assets/logo.png";

interface HeroSectionProps {
  user: any;
  onGetStarted: () => void;
}

export const HeroSection = ({ user, onGetStarted }: HeroSectionProps) => {
  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Gradient mesh background */}
      <div className="absolute inset-0 gradient-mesh" />
      
      {/* Floating orbs */}
      <div className="absolute top-1/4 left-[10%] w-64 h-64 md:w-96 md:h-96 bg-primary/20 rounded-full blur-[100px] animate-pulse-glow" />
      <div className="absolute bottom-1/4 right-[10%] w-48 h-48 md:w-80 md:h-80 bg-accent/15 rounded-full blur-[80px] animate-pulse-glow" style={{ animationDelay: '1s' }} />
      
      {/* Subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.015)_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,black,transparent)]" />

      <div className="container relative z-10 px-4 py-16 md:py-24">
        <div className="max-w-5xl mx-auto text-center space-y-6 md:space-y-8">
          {/* Logo */}
          <div className="flex justify-center mb-6 md:mb-10 animate-slide-up" style={{ animationDelay: '0.1s', opacity: 0 }}>
            <div className="relative">
              <div className="absolute inset-0 bg-primary/30 blur-3xl rounded-full scale-150" />
              <img src={logo} alt="KOSH" className="relative h-20 w-20 md:h-32 md:w-32 drop-shadow-2xl animate-float" fetchPriority="high" loading="eager" width={128} height={128} />
            </div>
          </div>

          {/* Badge */}
          <div className="animate-slide-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              <span className="text-xs md:text-sm font-medium text-foreground/80">
                Built for Indian Small Businesses
              </span>
            </div>
          </div>

          {/* Headline */}
          <div className="space-y-4 animate-slide-up" style={{ animationDelay: '0.3s', opacity: 0 }}>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.05] tracking-tight text-balance">
              Know Your Business,
              <br className="hidden sm:block" />
              <span className="text-gradient"> Every Single Day</span>
            </h1>
          </div>

          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed animate-slide-up px-2" style={{ animationDelay: '0.4s', opacity: 0 }}>
            The simplest way to track daily sales, expenses, and profit for your 
            <span className="text-foreground font-medium"> kirana, salon, café, </span>
            or service business.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-4 md:pt-6 animate-slide-up px-4" style={{ animationDelay: '0.5s', opacity: 0 }}>
            <Button 
              size="lg" 
              onClick={onGetStarted}
              className="group text-base md:text-lg h-12 md:h-14 px-8 md:px-10 rounded-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 w-full sm:w-auto"
            >
              {user ? "Go to Dashboard" : "Start Free Today"}
              <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-base md:text-lg h-12 md:h-14 px-8 md:px-10 rounded-full border-2 border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 w-full sm:w-auto"
            >
              <Sparkles className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              See Features
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="pt-8 md:pt-12 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-xs md:text-sm text-muted-foreground animate-slide-up" style={{ animationDelay: '0.6s', opacity: 0 }}>
            {[
              "No credit card needed",
              "Free forever plan", 
              "2-minute daily close"
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-success" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats section */}
        <div className="max-w-4xl mx-auto mt-16 md:mt-24 animate-slide-up" style={{ animationDelay: '0.7s', opacity: 0 }}>
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            {[
              { value: "60s", label: "Daily Close" },
              { value: "100%", label: "Free Plan" },
              { value: "24/7", label: "Access" },
            ].map((stat, i) => (
              <div key={i} className="text-center p-4 md:p-6 rounded-2xl glass">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold text-gradient mb-1 md:mb-2">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <div className="w-1 h-2 rounded-full bg-muted-foreground/50" />
        </div>
      </div>
    </section>
  );
};